import React from "react";
import { useGetWithdrawalRequestsQuery } from "../../../../../../store/admin/contribution";
import { formatMoney } from "../../../../../../utils/currencyFormat";
import moment from "moment";
import { convertToTitleCase } from "../../../../../../utils/helpers";
import TableSkeletonLoader from "../../../../../../components/Common/TableSkeletonLoader";

const ViewWithdrawalDetailsModal = ({ requestId, onClose }) => {
  // Fetch requests from all statuses to find the specific one
  // We'll fetch a reasonable number of pages to find the request
  const { data: allData } = useGetWithdrawalRequestsQuery({
    status: "all",
    page: 1,
    limit: 100,
  });

  const { data: pendingData } = useGetWithdrawalRequestsQuery({
    status: "pending",
    page: 1,
    limit: 100,
  });

  const { data: approvedData } = useGetWithdrawalRequestsQuery({
    status: "approved",
    page: 1,
    limit: 100,
  });

  const { data: declinedData } = useGetWithdrawalRequestsQuery({
    status: "declined",
    page: 1,
    limit: 100,
  });

  const { data: paidData } = useGetWithdrawalRequestsQuery({
    status: "paid",
    page: 1,
    limit: 100,
  });

  const request = React.useMemo(() => {
    if (!requestId) return null;
    
    // Search through all fetched data
    const allRequests = [
      ...(allData?.withdrawal_requests || []),
      ...(pendingData?.withdrawal_requests || []),
      ...(approvedData?.withdrawal_requests || []),
      ...(declinedData?.withdrawal_requests || []),
      ...(paidData?.withdrawal_requests || []),
    ];
    
    // Remove duplicates by id
    const uniqueRequests = Array.from(
      new Map(allRequests.map((req) => [req.id, req])).values()
    );
    
    return uniqueRequests.find((req) => req.id === requestId);
  }, [requestId, allData, pendingData, approvedData, declinedData, paidData]);

  const isFetching = !allData && !pendingData && !approvedData && !declinedData && !paidData;

  const getStatusColor = (status) => {
    const statusUpper = (status || "").toUpperCase();
    if (statusUpper === "PAID" || statusUpper === "APPROVED") {
      return "#19BC31";
    }
    if (statusUpper === "PENDING") {
      return "#FFA500";
    }
    if (statusUpper === "DECLINED" || statusUpper === "CANCELED") {
      return "#FF4343";
    }
    return "#A1A0A0";
  };

  if (isFetching) {
    return (
      <div className="w-full py-5 px-3 px-lg-4">
        <TableSkeletonLoader rows={5} columns={1} />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="w-full py-5 px-3 px-lg-4">
        <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
          <h5 className="fw-bold text-black1 mb-0" style={{ fontSize: "20px" }}>
            Withdrawal Details
          </h5>
          <img
            src="/Icon/cancel.svg"
            role="button"
            onClick={onClose}
            alt="close"
            className="close-img h-5"
            style={{ cursor: "pointer" }}
          />
        </div>
        <p className="text-muted">Withdrawal request not found</p>
      </div>
    );
  }

  return (
    <div className="w-full py-5 px-3 px-lg-4">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
        <h5 className="fw-bold text-black1 mb-0" style={{ fontSize: "20px" }}>
          Withdrawal Request Details
        </h5>
        <img
          src="/Icon/cancel.svg"
          role="button"
          onClick={onClose}
          alt="close"
          className="close-img h-5"
          style={{ cursor: "pointer" }}
        />
      </div>

      <div className="w-full d-flex flex-column gap-3">
        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Member Name</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {request.member?.first_name && request.member?.last_name
              ? `${request.member.first_name} ${request.member.last_name}`
              : request.member?.name || "N/A"}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Membership Number</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {request.member?.member_no ? `#${request.member.member_no}` : "N/A"}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Amount</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {formatMoney(request.amount || 0, true, "₦")}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Status</p>
          <span
            className="px-3 py-1 rounded-pill text-white fw-semibold"
            style={{
              backgroundColor: getStatusColor(request.status),
              fontSize: "12px",
            }}
          >
            {convertToTitleCase(request.status || "PENDING")}
          </span>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Request Date</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {moment(request.created_at).format("DD-MMM-YYYY")}
          </p>
        </div>

        <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
          <p className="text-sm text-muted mb-0">Request Time</p>
          <p className="text-sm fw-bold text-black1 mb-0">
            {moment(request.created_at).format("hh:mm A")}
          </p>
        </div>

        {request.bank_account && (
          <>
            <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
              <p className="text-sm text-muted mb-0">Bank Name</p>
              <p className="text-sm fw-bold text-black1 mb-0">
                {request.bank_account?.bank_name ||
                  request.bank_account?.bank?.name ||
                  "N/A"}
              </p>
            </div>

            <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
              <p className="text-sm text-muted mb-0">Account Number</p>
              <p className="text-sm fw-bold text-black1 mb-0 font-monospace">
                {request.bank_account?.account_number || "N/A"}
              </p>
            </div>

            <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
              <p className="text-sm text-muted mb-0">Account Name</p>
              <p className="text-sm fw-bold text-black1 mb-0">
                {request.bank_account?.account_name || "N/A"}
              </p>
            </div>
          </>
        )}

        {request.description && (
          <div className="w-100 pb-3 border-bottom d-flex flex-column">
            <p className="text-sm text-muted mb-2">Description</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {request.description}
            </p>
          </div>
        )}

        {request.decline_reason && (
          <div className="w-100 pb-3 border-bottom d-flex flex-column">
            <p className="text-sm text-muted mb-2">Decline Reason</p>
            <p className="text-sm fw-bold text-danger mb-0">
              {request.decline_reason}
            </p>
          </div>
        )}

        {request.approved_by && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Approved By</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {request.approved_by?.name ||
                request.approved_by?.first_name ||
                "N/A"}
            </p>
          </div>
        )}

        {request.declined_by && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Declined By</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {request.declined_by?.name ||
                request.declined_by?.first_name ||
                "N/A"}
            </p>
          </div>
        )}

        {request.paid_by && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Paid By</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {request.paid_by?.name ||
                request.paid_by?.first_name ||
                "N/A"}
            </p>
          </div>
        )}

        {request.payment_date && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Payment Date</p>
            <p className="text-sm fw-bold text-black1 mb-0">
              {moment(request.payment_date).format("DD-MMM-YYYY")}
            </p>
          </div>
        )}

        {request.payment_reference && (
          <div className="w-100 pb-3 border-bottom d-flex justify-content-between align-items-center">
            <p className="text-sm text-muted mb-0">Payment Reference</p>
            <p className="text-sm fw-bold text-black1 mb-0 font-monospace">
              {request.payment_reference}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewWithdrawalDetailsModal;

